import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="signature-pad"
export default class extends Controller {
  static targets = ["canvas", "input", "clearButton", "submitButton", "preview", "signatureMethod", "typedNameInput"]

  connect() {
    this.canvas = this.canvasTarget
    this.ctx = this.canvas.getContext("2d")
    this.isDrawing = false
    this.hasDrawn = false

    // Set canvas size
    this.resizeCanvas()
    window.addEventListener("resize", this.resizeCanvas.bind(this))

    // Setup drawing
    this.setupCanvas()
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeCanvas.bind(this))
  }

  resizeCanvas() {
    // Store current signature if any
    const imageData = this.hasDrawn ? this.canvas.toDataURL() : null

    // Set canvas size to match container
    const rect = this.canvas.getBoundingClientRect()
    this.canvas.width = rect.width
    this.canvas.height = rect.height

    // Restore signature if it existed
    if (imageData) {
      const img = new Image()
      img.onload = () => {
        this.ctx.drawImage(img, 0, 0)
      }
      img.src = imageData
    }
  }

  setupCanvas() {
    // Mouse events
    this.canvas.addEventListener("mousedown", this.startDrawing.bind(this))
    this.canvas.addEventListener("mousemove", this.draw.bind(this))
    this.canvas.addEventListener("mouseup", this.stopDrawing.bind(this))
    this.canvas.addEventListener("mouseout", this.stopDrawing.bind(this))

    // Touch events for mobile
    this.canvas.addEventListener("touchstart", this.handleTouchStart.bind(this))
    this.canvas.addEventListener("touchmove", this.handleTouchMove.bind(this))
    this.canvas.addEventListener("touchend", this.stopDrawing.bind(this))

    // Configure drawing style
    this.ctx.strokeStyle = "#000000"
    this.ctx.lineWidth = 2
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"
  }

  handleTouchStart(e) {
    e.preventDefault()
    const touch = e.touches[0]
    const mouseEvent = new MouseEvent("mousedown", {
      clientX: touch.clientX,
      clientY: touch.clientY
    })
    this.canvas.dispatchEvent(mouseEvent)
  }

  handleTouchMove(e) {
    e.preventDefault()
    const touch = e.touches[0]
    const mouseEvent = new MouseEvent("mousemove", {
      clientX: touch.clientX,
      clientY: touch.clientY
    })
    this.canvas.dispatchEvent(mouseEvent)
  }

  startDrawing(e) {
    this.isDrawing = true
    const rect = this.canvas.getBoundingClientRect()
    this.ctx.beginPath()
    this.ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top)
  }

  draw(e) {
    if (!this.isDrawing) return

    const rect = this.canvas.getBoundingClientRect()
    this.ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top)
    this.ctx.stroke()

    this.hasDrawn = true
    this.updateSignatureData()
  }

  stopDrawing() {
    if (this.isDrawing) {
      this.isDrawing = false
      this.ctx.closePath()
    }
  }

  clear() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.hasDrawn = false
    this.inputTarget.value = ""
    this.updateSubmitButton()
  }

  updateSignatureData() {
    if (this.hasDrawn) {
      // Convert canvas to base64 PNG
      const dataURL = this.canvas.toDataURL("image/png")
      this.inputTarget.value = dataURL

      // Show preview if target exists
      if (this.hasPreviewTarget) {
        this.previewTarget.src = dataURL
        this.previewTarget.classList.remove("hidden")
      }
    }

    this.updateSubmitButton()
  }

  updateSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      const hasSignature = this.hasDrawn && this.inputTarget.value !== ""
      const signatureMethod = this.hasSignatureMethodTarget ? this.signatureMethodTarget.value : "drawn"
      const hasTypedName = this.hasTypedNameInputTarget && this.typedNameInputTarget.value.trim() !== ""

      // Enable submit if either drawn signature OR typed name is provided
      const canSubmit = (signatureMethod === "drawn" && hasSignature) || (signatureMethod === "typed" && hasTypedName)

      this.submitButtonTarget.disabled = !canSubmit
    }
  }

  switchToTyped() {
    if (this.hasSignatureMethodTarget) {
      this.signatureMethodTarget.value = "typed"
    }

    // Show/hide sections
    const drawnSection = document.getElementById('drawn-signature-section')
    const typedSection = document.getElementById('typed-signature-section')
    if (drawnSection) drawnSection.classList.add('hidden')
    if (typedSection) typedSection.classList.remove('hidden')

    this.updateSubmitButton()
  }

  switchToDrawn() {
    if (this.hasSignatureMethodTarget) {
      this.signatureMethodTarget.value = "drawn"
    }

    // Show/hide sections
    const drawnSection = document.getElementById('drawn-signature-section')
    const typedSection = document.getElementById('typed-signature-section')
    if (typedSection) typedSection.classList.add('hidden')
    if (drawnSection) drawnSection.classList.remove('hidden')

    this.updateSubmitButton()
  }

  // Called when typed name input changes
  typedNameChanged() {
    this.updateSubmitButton()
  }
}
